<template lang="pug">
  .product-invoice
    y-dialog(fill-height fluid v-model="showModal" scrollable :fullscreen="$vuetify.breakpoint.xsOnly" transition="fade-transition" persistent max-width="600px")
      v-card(min-height="80vh")
        v-card-title.px-3.white
          v-row(no-gutters align="center")
            v-col.shrink
              v-btn.ml-n3(text icon @click="showModal = !showModal" height)
                v-icon(color="basic800" :size="24") mdi-close
            v-col.ml-3(v-if="!isFetching")
              div.d-flex
                .h6.basic800--text.my-auto {{ invoiceName }} &#35;{{ lead.rid }}
                v-spacer
                v-chip.text-uppercase.my-auto.button-AA-tiny(small outlined label :color="lead.status | leadStatusColor" :style="{ 'background-color': chipBackgroundColor(lead.status) + '!important' }" ) {{ lead.status | leadStatusDisplay }}
                v-menu(left, offset-y)
                  template(v-slot:activator="{ on }")
                    v-btn.mr-1(
                      text,
                      icon,
                      min-width="auto",
                      :ripple="false",
                      v-on="on"
                    )
                      eva-icon(
                        name="more-vertical",
                        :fill="$vuetify.theme.themes[$theme].primary500",
                        height="24",
                        width="24"
                      )
                  v-list(dense)
                    v-list-item(v-if="yBooksSubscription.limitations.reminder > 0 && invoiceName == 'Invoice'" @click="clickScheduleReminder()")
                      v-list-item-content
                        v-list-item-title.s2.basic800--text Schedule reminder
                    v-list-item(@click="printReceipt()")
                      v-list-item-content
                        v-list-item-title.s2.basic800--text Print {{ invoiceName.toLowerCase() }}

        v-layout.my-6(v-if="isFetching")
          v-row.fill-height.ma-0(align='center' justify='center')
            v-progress-circular.ma-auto(:width="2" :size="50" color="primary" indeterminate)
        v-card-text.pa-0.text-center(v-else)
          v-container.pt-4
            div
              eva-icon(name="checkmark-circle-2-outline" :fill="lead.paid ? $vuetify.theme.themes[$theme].success500 : $vuetify.theme.themes[$theme].basic600T48" height="60" width="60")
              p.s1.mb-0.mt-5(:class="lead.paid ? 'primary500--text' : 'danger500--text'") {{ lead.paid ? 'Amount paid' : 'Balance due' }}
              template(v-if="lead.paid")
                div.h4.primary500--text.text-center {{ totalPaid | displayPrice }}
                div.s1.primary400--text.mt-1 {{ lead.paid_at | convertServerDate }}
                v-btn.button-Aa-small.custom-transform-class.text-none.mt-2(v-if="hasTackingCode" color="textColorAccent" text  @click="showParcelHistoryDialog = true" small)
                  v-icon.mr-2.c1(color="textColorAccent" small) mdi-map-marker-radius-outline
                  | track your parcel
              template(v-else)
                div.h4.primary500--text.text-center {{ amountDue | displayPrice }}
                template(v-if="installmentEnabled && !income.quotation")
                  div.d-flex.justify-center
                    y-checkbox.mt-1(center v-model="lead.installment" dense hide-details @change="updateInstallment" color="black")
                      template(v-slot:label)
                        div.s1 Allow partial payment
          
            v-divider.basic300.my-6
            .company-details.text-left.mt-4(v-if="company")
              div.d-flex
                div.s1.primary500--text {{ company.name }}
                v-btn.button-AA-tiny.px-1.ml-3(v-if="!company.address" color="textColorAccent" outlined x-small height="24" :style="{'background-color': $vuetify.theme.themes[$theme].infoT08}" @click="showUpdateCompanyDetails = !showUpdateCompanyDetails")
                  v-icon.mr-1(x-small) mdi-pencil
                  | Edit
              div.p2.primary500--text {{ company.phone }}
              div.p2.primary500--text(v-if="company.address")
                div {{ company.address.address_1 }},
                div {{ company.address.address_2 }}, {{ company.address.city }},
                div {{ company.address.postcode }}, {{ company.address.state }}, {{ company.address.country }}.

            .client-details.text-left.mt-8
              div.p1.primary500--text Bill to:
              div.s1.primary500--text {{ lead.name }}
              div.p2.primary500--text(v-if="lead.email") {{ lead.email }}
              div.p2.primary500--text {{ lead.phone | phoneFormat }}

              div.mt-5(v-if="lead.shipping_address && !lead.pickup")
                div.p1.primary500--text Shipping address:
                div.p2.primary500--text {{ lead.shipping_address.address_1 }},
                div.p2.primary500--text {{ lead.shipping_address.address_2 }}, {{ lead.shipping_address.city }},
                div.p2.primary500--text {{ lead.shipping_address.postcode }}, {{ lead.shipping_address.state }}, {{ lead.shipping_address.country }}.

              div.pt-4(v-if="!lead.paid")
                v-btn.button-AA-tiny.white--text(v-if="lead.shipping_address" color="info500" outlined x-small depressed height="24" @click="showUpdateCustomerAddress = true")
                  v-icon(left x-small) mdi-pencil
                  | edit address
                v-btn.button-AA-tiny.white--text(v-if="!lead.shipping_address" color="info500" x-small depressed height="24" @click="showUpdateCustomerAddress = true")
                  v-icon(left small) mdi-plus
                  | add address
                div.mt-2.c1.primary400--text *Required for order with delivery

            v-divider.my-4.basic300

            v-simple-table(dense)
              thead
                tr
                  th.pa-0(style="border: none")
                    div.s1.primary400--text.text-start.mr-1 Description
                  th.pa-0(style="border: none")
                    div.s1.primary400--text.text-end.mx-1 Price
                  th.pa-0(style="border: none")
                    div.s1.primary400--text.text-end.ml-1 Amount
              tbody
                template(v-for="p in lead.products")
                  tr(:key="p.id")
                    td.text-start.pa-0(style="border: none; vertical-align:top;")
                      div.my-1
                        div.p2.primary500--text.mr-1 {{ p.quantity }} x {{ p.name }} {{ p.addons.length > 0 ? ' set' : '' }}
                        div.c1.danger500--text(v-if="p.inventory_enabled && p.inventory_qty < p.quantity && !lead.paid") {{ p.inventory_qty > 0 ? `Only ${p.inventory_qty} left in stock` : 'Sold out' }}
                        div.ml-3
                          template(v-if="p.addons.length > 0")
                            div.c1.primary300--text.mr-1 {{ p.name }}
                            div.c1.danger500--text(v-if="p.inventory_enabled && p.inventory_qty < p.quantity && !lead.paid") {{ p.inventory_qty > 0 ? `Only ${p.inventory_qty} left in stock` : 'Sold out' }}
                          template(v-for="a in p.addons")
                            div.c1.primary300--text.mr-1 + {{ a.name }}{{ a.quantity_per_set > 1 ? ' x ' + a.quantity_per_set : '' }}
                            div.c1.danger500--text(v-if="a.inventory_enabled && a.inventory_qty < a.quantity && !lead.paid") {{ a.inventory_qty > 0 ? `Only ${a.inventory_qty} left in stock` : 'Sold out' }}
                          template(v-if="p.description_visibility.includes('invoice')")
                            .c1.primary300--text.mr-1 {{ p.description }}
                    td.text-end.pa-0(style="border: none; min-width: 70px; vertical-align:top;")
                      div.my-1
                        div.p2.primary500--text.mx-1 {{ p.price_per_set | displayPrice }}
                        template(v-if="p.addons.length > 0")
                          div.c1.primary300--text.mx-1 {{ p.price | displayPrice }}
                        template(v-for="a in p.addons")
                          div.c1.primary300--text.mx-1 {{ a.price * a.quantity_per_set | displayPrice }}
                    td.text-end.pa-0(style="border: none; min-width: 70px; vertical-align:top;")
                      div.my-1.p2.primary500--text.ml-1 {{ p.price_per_set * p.quantity | displayPrice }}

            div.my-6.dotted
            div
              div.d-flex.mt-1(v-if="discountPrice")
                div.p1.primary400--text Total Product Discount:
                v-spacer
                div.p1.primary400--text - {{ discountPrice | displayPrice }}
              div.d-flex.mt-1
                div.p1.primary400--text Subtotal:
                v-spacer
                div.p1.primary400--text {{ subtotalPrice | displayPrice }}
              div.d-flex.mt-1(v-if="invoiceDiscountPrice")
                div.p1.primary400--text Discount:
                v-spacer
                div
                  v-layout
                    v-spacer
                    div.p1.primary400--text - {{ invoiceDiscountPrice | displayPrice }}
                    template(v-if="isEditable" style="cursor:pointer")
                      v-icon.ml-1(small color="danger500" @click="handleDeleteDiscount()") mdi-delete-outline
              div.d-flex.mt-1(v-else-if="!lead.paid && isEditable")
                div.p1.primary400--text Discount:
                v-spacer
                div.button-Aa-medium.my-auto.text-right.info500--text(style="width:120px; cursor:pointer" @click="showEditDiscount = true") Add discount
              div.d-flex.mt-1
                div.p1.primary400--text Tax:
                v-spacer
                div.p1.primary400--text {{ taxAmount }}%
              div.d-flex.align-center.mt-1(v-if="shippingFee !== null")
                div.p1.primary400--text Delivery Charge:
                v-spacer
                div.p1.primary400--text(v-if="shippingFee === 0")
                  v-chip.px-1(color="success500" small label outlined)
                    v-icon(small).pr-1 mdi-truck-fast
                    | Free Shipping
                div.p1.primary400--text(v-else) {{ shippingFee | displayPrice }}
              div.d-flex.mt-1
                div.h6.primary--text Total:
                v-spacer
                div.h6.primary--text {{ totalPrice | displayPrice }}
              div.d-flex.mt-1
                div.p1.primary400--text Amount Paid:
                v-spacer
                div.p1.primary400--text {{ totalPaid | displayPrice }}

              .due-date(v-if="!lead.paid")
                div.d-flex.py-2.ml-n2.mr-n2.mt-1(:style="`background-color: ${$vuetify.theme.themes[$theme].dangerT16}; border-radius: 4px`")
                  div.s1.primary--text.ml-2 Balance Due:
                  v-spacer
                  div.s1.primary--text.mr-2 {{ amountDue | displayPrice }}
                div.d-flex.mt-2(v-if="income.due_date || income.quotation_due_date")
                  div.c1.primary400--text
                    span(v-if="income.quotation && income.quotation_due_date") This quotation expires on: {{ income.quotation_due_date | humanizeDate }}
                    span(v-else-if="income.due_date") Please complete payment before: {{ income.due_date | humanizeDate }}
            
            div.mt-12.text-left(v-if="income.notes")
              .p2.primary400--text Notes:
              .p2.primary400--text.mt-2 {{ income.notes }}
            v-layout.mt-6(v-else)
              v-btn.button-AA-tiny.textColor--text(color="basic300" x-small depressed height="24" @click="showAddInvoiceNoteDialog = true")
                v-icon(left small) mdi-plus
                | add notes/terms
              
            div.mt-12.mb-6
              div.f1.primary400--text {{ $t('powered_by_yezza') }}
              div.f2.primary400--text(v-if="lead.paid") This is a computer generated receipt. No signature required.
              div.f2.primary400--text(v-else) This is a computer generated invoice. No signature required.

        v-card-actions.px-3.pb-3
          v-row(no-gutters)
            v-col.pr-1(cols="6")
              v-btn.px-1(v-if="income.payment_status != 'paid'" outlined height=40 @click='clickRecordPayment' block color="info500" style="background-color: var(--v-infoT08)")
                .button-AA-small.info500--text {{ income.quotation ? 'update quotation' : 'record payment' }}
              v-layout(v-else)
                v-spacer
                v-btn.px-1.mr-2(color='white', text @click='showModal = false' height=40)
                  .button-Aa-small.primary400--text Close
            v-col.pl-1(cols="6")
              v-btn.px-1(color='info500' depressed height=40 @click='shareSheet = true' block)
                .button-AA-small.basic100--text Share {{ invoiceName }}

    .modals
      parcel-history-dialog(v-if="showParcelHistoryDialog" :open.sync="showParcelHistoryDialog" :lead="lead" :shipping="lead.shipping")
      update-company-details(v-if="showUpdateCompanyDetails" :open.sync="showUpdateCompanyDetails" :company.sync="company")
      add-customer-address(v-if="showUpdateCustomerAddress" :open.sync="showUpdateCustomerAddress" :parentLead.sync="lead")
      AddDiscount(v-if="showEditDiscount" :open.sync="showEditDiscount" :initialDiscount.sync="invoiceDiscount" @added="handleAddDiscount")
      RecordPayment(v-if="showRecordPaymentDialog" :open.sync="showRecordPaymentDialog" :parentIncome.sync="income")
      schedule-reminder(v-if="showScheduleReminderDialog" :open.sync="showScheduleReminderDialog" type="income" :selectedInvoices="[income]")
      y-bottom-sheet(v-model='shareSheet' inset max-width="600px")
        v-list
          div.pt-2.px-6.s2.primary400--text Share url by
          v-list-item(@click="clickSendInvoice()")
            v-list-item-avatar.my-0
              v-icon(color="#455A64" size="24") fab fa-whatsapp
            v-list-item-title.s2.basic1000--text WhatsApp
          v-list-item(v-clipboard:copy="getShareLinkURL()" v-clipboard:success="copyUrl")
            v-list-item-avatar.my-0
              v-icon(size="24") mdi-content-copy
            v-list-item-title.s2.basic1000--text Copy Link
              div.c1.primary400--text {{getShareLinkURL()}}
      y-dialog(v-model='showAddInvoiceNoteDialog', max-width='500' persistent)
        v-card
          v-card-title.h6.basic800--text.px-4 Add Notes or terms
          v-card-text
            v-form(ref="form")
              .notes
                .label.basic600--text.text-uppercase Notes/terms
                v-textarea.mt-2(v-model='form.notes' placeholder="Write notes or terms here" background-color="basic200" hide-details outlined dense :maxlength="200")
                .guide.mt-3.d-flex
                  eva-icon.mr-2(name="info-outline" :fill="$vuetify.theme.themes[$theme].basic600" height="15" width="15")
                  div.c1.basic600--text Your customers will see this in their invoices
          v-card-actions.pb-3
            v-spacer
            v-btn(color='white' text @click='showAddInvoiceNoteDialog = false')
              .button-Aa-small.primary400--text Cancel
            v-btn.mr-3(color='info500' depressed dark @click='saveNotes()' :loading="isLoading")
              .button-AA-small.basic100--text Save
      y-dialog(v-model='showConvertInvoiceDialog', max-width='400' persistent)
        v-card
          v-card-title.h6.basic800--text.px-4 Set invoice payment due
          v-card-text
            v-form(ref="form")
              .payment-due
                .label.basic600--text.text-uppercase.mt-4 Payment due
                v-dialog(v-model='form.date.show' width='290px')
                  template(v-slot:activator='{ on }')
                    v-text-field.mt-2(:value="form.date.value" placeholder="Select date" outlined dense background-color="basic200" readonly v-on='on' :rules="rules.is_selected" hide-details)
                      template(v-slot:append)
                        eva-icon(name="chevron-down-outline" width="24" height="24" :fill="$vuetify.theme.themes[$theme].basic700")
                  v-date-picker(v-model='form.paymentDueDate' color="primary" no-title @change="saveDate()")
          v-card-actions.pb-3
            v-spacer
            v-btn(color='white' text @click='showConvertInvoiceDialog = false')
              .button-Aa-small.primary400--text Cancel
            v-btn.mr-3(color='info500' depressed dark @click='startConvertToInvoice()' :loading="isLoading")
              .button-AA-small.basic100--text Convert

</template>

<script>
import { mapState, mapActions } from 'vuex'
import UpdateCompanyDetails from '@/components/dialogs/settings/UpdateCompanyDetails.vue'
import AddCustomerAddress from '@/components/dialogs/database/AddCustomerAddress.vue'
import ParcelHistoryDialog from '@/views/invoice/components/ParcelHistoryDialog'
import RecordPayment from '@/components/yezzabook/dialogs/income/RecordPayment.vue'
import ScheduleReminder from '@/components/yezzabook/dialogs/ScheduleReminder.vue'
import AddDiscount from '@/components/yezzabook/dialogs/income/addincome/AddDiscount.vue'


export default {
  name: 'IncomeInvoiceDialog',
  props: ['open', 'parentIncome'],
  components: { UpdateCompanyDetails, AddCustomerAddress, AddDiscount, ParcelHistoryDialog, RecordPayment, ScheduleReminder },
  data: () => ({
    shareSheet: false,
    isFetching: true,
    isLoading: false,
    lead: null,
    invoice: null,
    showRecordPaymentDialog: false,
    showParcelHistoryDialog: false,
    showUpdateCompanyDetails: false,
    showUpdateCustomerAddress: false,
    showApplyCouponDialog: false,
    showConvertInvoiceDialog: false,
    showAddInvoiceNoteDialog: false,
    showScheduleReminderDialog: false,
    showEditDiscount: false,
    invoiceDiscount: null,
    company: null,
    form: {
      notes: null,
      paymentDueDate: null,
      date: {
        show: false,
        value: null
      }
    },
    rules: {
      is_selected: [(v) => v !== null || 'This field is required'],
      not_empty: [val => (val || '').length > 0 || 'This field is required']
    }
  }),
  computed: {
    ...mapState('subscriptions', ['yBooksSubscription']),
    showModal: {
      get() {
        return this.open
      },
      set(newValue) {
        this.$emit('update:open', newValue)
      }
    },
    income: {
      get() {
        return this.parentIncome
      },
      set(newValue) {
        this.getInvoice()
        this.$emit('update:parentIncome', newValue)
      }
    },
    isEditable () {
      if (this.lead.paid) {
        return false
      }
      if (this.lead.cod) {
        return false
      }
      if (this.lead.installment && this.lead.status == 'partial_payment') {
        return false
      }
      return true
    },
    invoiceName () {
      if (this.income.payment_status === 'paid') {
        return 'Receipt'
      }
      return this.income.quotation ? 'Quotation' : 'Invoice'
    },
    shippingFee () {
      if (!this.invoice) return 0
      return this.invoice.shipping_fee
    },
    discountPrice () {
      if (!this.invoice) return 0
      return this.invoice.total_discount
    },
    invoiceDiscountPrice() {
      if (!this.invoice) return 0
      return this.invoice.invoice_discount
    },
    subtotalPrice () {
      if (!this.invoice) return 0
      return this.invoice.subtotal_amount
    },
    totalPrice () {
      if (!this.invoice) return 0
      return this.invoice.total_amount
    },
    totalPaid () {
      if (!this.invoice) return 0
      return this.invoice.total_paid
    },
    amountDue () {
      if (!this.invoice) return 0
      return this.invoice.total_amount - this.invoice.total_paid
    },
    taxAmount () {
      if (!this.invoice) return 0
      return this.invoice.company.tax_rate
    },
    hasTackingCode () {
      if (this.lead.shipping && (this.lead.shipping.tracking_id || this.lead.shipping.shipping_method === 'easyparcel')) {
        return true
      }
      return false
    },
    installmentEnabled () {
      if (this.lead.form.invoice_settings && this.lead.form.invoice_settings.extra_settings) {
        return this.lead.form.invoice_settings.extra_settings.installment_enabled
      }
      return false
    },
    totalInvoiceDiscount () {
      if (!this.invoiceDiscount) {
        return 0
      }
      let discountAmount = this.invoiceDiscount.value
      if (this.invoiceDiscount.isPercentage) {
        discountAmount = this.invoiceDiscount.value * (this.form.type == 'amount' ? this.form.data.value : this.totalPrice) / this.$currency.multiplier
      }
      return parseInt(discountAmount)
    }
  },
  mounted () {
    this.$axios.get(`/leads/${this.income.pid}/`)
      .then((response) => {
        this.lead = response.data
        this.getInvoice()
        this.fetchCompanyDetails()
        if (this.hasTackingCode) {
          this.updateTracking()
        }
      })
      .catch(() => {})
  },
  methods: {
    ...mapActions([
      'showMessage'
    ]),
    getInvoice () {
      this.isFetching = true
      this.$axios.get(`/invoices/${this.lead.pid}/`)
        .then((response) => {
          this.isFetching = false
          this.invoice = response.data
          if (this.invoice.paid) {
            this.lead.paid = true
            if (this.lead.status !== 'received' && this.lead.status !== 'shipped' && this.lead.status !== 'cancelled') {
              this.lead.status = 'processed'
            }
          } else if (this.invoice.total_paid > 0) {
            this.lead.status = 'partial_payment'
          }
        })
        .catch(() => {})
    },
    fetchCompanyDetails () {
      this.$axios.get(`/companies/${this.lead.company}/`).
        then((response) => {
          this.company = response.data
        })
        .catch(() => {})
    },
    getShareLinkURL () {
      let path = 'invoices'
      if (this.lead && this.lead.paid) {
        path = 'receipts'
      }
      if (this.income.quotation) {
        path = 'quotations'
      }
      return `${this.$subdomain}.${process.env.VUE_APP_BASE_DOMAIN}/${path}/${this.lead.pid}`
    },
    clickConvertInvoice () {
      this.showConvertInvoiceDialog = true
      if (this.income.quotation_due_date) {
        this.form.paymentDueDate = this.income.quotation_due_date
        this.form.data.date.value = this.formatDate(this.form.paymentDueDate)
      }
    },
    handleAddDiscount (val) {
      this.invoiceDiscount = val
      this.updateDiscount()
    },
    handleDeleteDiscount () {
      this.invoiceDiscount = null
      this.updateDiscount()
    },
    updateDiscount () {
      this.isFetching = true
      const data = { 
        invoice_discount: this.totalInvoiceDiscount
      }
      this.$axios.patch(`/invoices/${this.lead.pid}/`, data)
        .then((response) => {
          this.invoice = response.data
          this.$axios.get(`/books/${this.invoice.company.slug}/incomes/${this.income.pid}/?expand=category,products`)
            .then(resp => {
              this.income = resp.data
            }).catch(() => {})
        })
        .catch(() => {})
    },
    saveDate () {
      this.form.date.show = false
      this.form.date.value = this.formatDate(this.form.paymentDueDate)
    },
    formatDate (date) {
      if (!date) {
        return null
      }
      const [year, month, day] = date.split('-')
      return `${day}/${month}/${year}`
    },
    saveNotes () {
      if (!this.$refs.form.validate()) {
        return
      }
      this.updateIncome({ notes:  this.form.notes })
    },
    startConvertToInvoice () {
      if (!this.$refs.form.validate()) {
        return
      }
      this.updateIncome({ quotation: false, due_date:  this.form.paymentDueDate })
    },
    updateIncome (data) {
      this.isLoading = true
      this.$axios.patch(`/books/${this.invoice.company.slug}/incomes/${this.income.pid}/`, data)
        .then(resp => {
          this.income = resp.data
          this.showConvertInvoiceDialog = false
          this.showAddInvoiceNoteDialog = false
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoading = true
        })
    },
    clickRecordPayment () {
      this.showRecordPaymentDialog = true
    },
    copyUrl () {
      this.showMessage(['success', this.$t('success_message.link_copied_to_clipboard')])
      this.shareSheet = false
    },
    clickSendInvoice () {
      let url = `https://api.whatsapp.com/send?text=${this.getShareLinkURL()}`
      if (this.income.phone) {
        url = `https://api.whatsapp.com/send?phone=${this.$options.filters.phoneSanitized(this.income.phone)}&text=${this.getShareLinkURL()}`
      }
      window.open (url)
    },
    updateInstallment () {
      const params = {
        installment: this.lead.installment,
      }
      this.axios.patch(`/leads/${this.lead.pid}/`, params)
        .then(resp => {
          this.lead = resp.data
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
    },
    clickScheduleReminder () {
      this.showScheduleReminderDialog = true
    },
    printReceipt () {
      let printUrl = `https://pdf.yezza.io/?url=http://localhost:8080/print-invoices/${this.lead.pid}&type=pdf&filename=receipt.pdf&waitUntil=networkidle0&printBackground=true&format=A4`
      if (this.$subdomain) {
        printUrl = `https://pdf.yezza.io/?url=${this.$subdomain}.yezza.io/print-invoices/${this.lead.pid}&type=pdf&filename=receipt.pdf&waitUntil=networkidle0&printBackground=true&format=A4`
      }
      if (this.$isMobile) {
        window.open(printUrl)
      } else {
        location.href = printUrl
      }
    },
    updateTracking () {
      this.axios.post(`/shipping/shippings/${this.lead.shipping.id}/update-tracking`, {}).catch(() => {})
    },
    chipBackgroundColor (status) {
      if (status === 'new') {
        return this.$vuetify.theme.themes[this.$theme].infoT08
      } else if (status === 'follow_up') {
        return this.$vuetify.theme.themes[this.$theme].warningT08
      } else if (status === 'partial_payment') {
        return this.$vuetify.theme.themes[this.$theme].basic600T08
      } else if (status === 'reject') {
        return this.$vuetify.theme.themes[this.$theme].dangerT08
      } else if (status === 'processed') {
        return this.$vuetify.theme.themes[this.$theme].successT08
      } else if (status === 'shipped') {
        return this.$vuetify.theme.themes[this.$theme].warningT08
      } else if (status === 'received') {
        return this.$vuetify.theme.themes[this.$theme].basic600T08
      } else if (status === 'cancelled') {
        return this.$vuetify.theme.themes[this.$theme].dangerT08
      } else if (status === 'error') {
        return this.$vuetify.theme.themes[this.$theme].dangerT08
      } else if (status === '') {
        return this.$vuetify.theme.themes[this.$theme].basic600T08
      }
      return this.$vuetify.theme.themes[this.$theme].basic600T08
    }
  }
}
</script>

<style scoped>
.dotted {
  border-top: 2px dashed var(--v-primary400);
}
.v-text-field--outlined >>> fieldset {
  border-color: var(--v-primary200);
}
.error--text >>> fieldset {
  border-color: var(--v-danger);
}
</style>

<i18n>
{
  "en": {
    "form_name": {
      "label": "Form Name",
      "placeholder": "Write form name here"
    },
    "customer_info": "What Info Do You Collect",
    "customer_expalain": "Check or add fileds that you want to appear in your customer form.",
    "customer_name": {
      "label": "Name",
      "placeholder": "Customer name"
    },
    "customer_phone": {
      "label": "Phone",
      "placeholder": "Customer phone no"
    },
    "customer_email": {
      "label": "Email",
      "placeholder": "Customer email"
    },
    "price": {
      "label": "Amount",
      "placeholder": "0"
    },
    "add_fields": "+ Collect Extra Info",
    "add_products": "+ Add Products",
    "send_to_customer": "Send to Customer"
  }
}
</i18n>
